import { impersonationID } from '../utils/impersonation';
/**
 * WebSocket telemetry store for real-time device data.
 * Connects to /api/devices/stream with auto-reconnect.
 */

type TelemetryEvent = {
  type: string;
  device_eui?: string;
  [key: string]: any;
};

type TelemetryState = {
  connected: boolean;
  latestEvent: TelemetryEvent | null;
};

type Subscriber = (state: TelemetryState) => void;

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000]; // exponential backoff caps at 15s

class TelemetryStore {
  private state: TelemetryState = { connected: false, latestEvent: null };
  private subscribers: Set<Subscriber> = new Set();
  private ws: WebSocket | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;

  constructor() {
    // Don't auto-connect, let components call connect()
  }

  subscribe(fn: Subscriber): () => void {
    this.subscribers.add(fn);
    fn(this.state);
    return () => this.subscribers.delete(fn);
  }

  private notify() {
    for (const fn of this.subscribers) {
      fn(this.state);
    }
  }

  /** Helper for unit tests to simulate incoming WebSocket events */
  emitTestEvent(event: TelemetryEvent) {
    this.state = { connected: true, latestEvent: event };
    this.notify();
  }

  connect(token?: string) {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return; // already connected or connecting
    }

    this.intentionalClose = false;

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let url = `${proto}//${window.location.host}/api/devices/stream`;
    if (token) {
      url += `?token=${encodeURIComponent(token)}`;
    }

    const impersonation = impersonationID();
    if (impersonation) url = url.replace('/api/devices/stream', `/api/acting/${encodeURIComponent(impersonation)}/devices/stream`);
    try {
      this.ws = new WebSocket(url);
    } catch (e) {
      console.error('[telemetry] WebSocket creation failed:', e);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.state = { ...this.state, connected: true };
      this.notify();
      console.log('[telemetry] Connected');
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as TelemetryEvent;
        this.state = { connected: true, latestEvent: data };
        this.notify();
      } catch (e) {
        // Non-JSON message, ignore
      }
    };

    this.ws.onclose = () => {
      this.state = { ...this.state, connected: false };
      this.notify();
      console.log('[telemetry] Disconnected');
      if (!this.intentionalClose) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (err) => {
      console.error('[telemetry] WebSocket error:', err);
      this.ws?.close();
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)];
    console.log(`[telemetry] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt + 1})`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempt++;
      this.connect();
    }, delay);
  }

  disconnect() {
    this.intentionalClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.state = { connected: false, latestEvent: null };
    this.notify();
  }
}

export const telemetry = new TelemetryStore();
